import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import AdminSidebar from "../../components/admin/AdminSidebar"
import AdminHeader from "../../components/admin/AdminHeader"
import UserFormModal from "../../components/admin/UserFormModal"
import UserViewModal from "../../components/admin/UserViewModal"
import UsersTable from "../../components/admin/UsersTable"
import LearnerDetailModal from "../../components/admin/learners/LearnerDetailModal"
import { useGet } from "../../utils/apiComponent/useApi"
import { endpoints } from "../../utils/endpoints"
import { toastUtils } from "../../utils/toast"
import { api } from "../../utils/apiComponent/apiClient"
import { 
  Search, 
  Filter, 
  Edit, 
  Trash2, 
  Eye, 
  Plus, 
  CheckCircle, 
  XCircle, 
  PauseCircle, 
  Ban,
  X,
  Phone,
  MapPin,
  Calendar,
  BookOpen,
  Award,
  Users,
  Download
} from "lucide-react"

export default function UserManagementPage() {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState("")
  const [filterRole, setFilterRole] = useState([])
  const [filterStatus, setFilterStatus] = useState([])
  const [filterGender, setFilterGender] = useState([])
  const [filterCountry, setFilterCountry] = useState("")
  const [filterCity, setFilterCity] = useState("")
  const [filterIsVerified, setFilterIsVerified] = useState(null)
  const [filterIsAdmin, setFilterIsAdmin] = useState(null)
  const [filterPreferredLanguage, setFilterPreferredLanguage] = useState("")
  const [filterCreatedFrom, setFilterCreatedFrom] = useState("")
  const [filterCreatedTo, setFilterCreatedTo] = useState("")
  const [filterUpdatedFrom, setFilterUpdatedFrom] = useState("")
  const [filterUpdatedTo, setFilterUpdatedTo] = useState("")
  const [filterBirthDateFrom, setFilterBirthDateFrom] = useState("")
  const [filterBirthDateTo, setFilterBirthDateTo] = useState("")
  const [filterHasEnrollments, setFilterHasEnrollments] = useState(null)
  const [filterHasCertificates, setFilterHasCertificates] = useState(null)
  const [filterHasPayments, setFilterHasPayments] = useState(null)
  const [filterEnrolledInCourseId, setFilterEnrolledInCourseId] = useState("")
  const [filterIncludeDeleted, setFilterIncludeDeleted] = useState(false)
  const [filterOnlyDeleted, setFilterOnlyDeleted] = useState(false)
  const [filterSortBy, setFilterSortBy] = useState("createdAt")
  const [filterSortOrder, setFilterSortOrder] = useState("desc")
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)
  const [isUserModalOpen, setIsUserModalOpen] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [viewingUser, setViewingUser] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(20)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [isExporting, setIsExporting] = useState(false)

  // Build query params from filters
  const buildQueryParams = () => {
    const params = new URLSearchParams()
    params.append('page', currentPage)
    params.append('limit', pageLimit)
    
    if (searchTerm) params.append('search', searchTerm)
    if (filterRole.length > 0) params.append('role', filterRole.join(','))
    if (filterStatus.length > 0) params.append('status', filterStatus.join(','))
    if (filterGender.length > 0) params.append('gender', filterGender.join(','))
    if (filterCountry) params.append('country', filterCountry)
    if (filterCity) params.append('city', filterCity)
    if (filterIsVerified !== null) params.append('isVerified', filterIsVerified)
    if (filterIsAdmin !== null) params.append('isAdmin', filterIsAdmin)
    if (filterPreferredLanguage) params.append('preferredLanguage', filterPreferredLanguage)
    if (filterCreatedFrom) params.append('createdFrom', filterCreatedFrom)
    if (filterCreatedTo) params.append('createdTo', filterCreatedTo)
    if (filterUpdatedFrom) params.append('updatedFrom', filterUpdatedFrom)
    if (filterUpdatedTo) params.append('updatedTo', filterUpdatedTo)
    if (filterBirthDateFrom) params.append('birthDateFrom', filterBirthDateFrom)
    if (filterBirthDateTo) params.append('birthDateTo', filterBirthDateTo)
    if (filterHasEnrollments !== null) params.append('hasEnrollments', filterHasEnrollments)
    if (filterHasCertificates !== null) params.append('hasCertificates', filterHasCertificates)
    if (filterHasPayments !== null) params.append('hasPayments', filterHasPayments)
    if (filterEnrolledInCourseId) params.append('enrolledInCourseId', filterEnrolledInCourseId)
    if (filterIncludeDeleted) params.append('includeDeleted', 'true')
    if (filterOnlyDeleted) params.append('onlyDeleted', 'true')
    params.append('sortBy', filterSortBy)
    params.append('sortOrder', filterSortOrder)
    
    return params.toString()
  }

  // Build export params (without pagination)
  const buildExportParams = (format) => {
    const params = new URLSearchParams()
    params.append('format', format)
    
    if (searchTerm) params.append('search', searchTerm)
    if (filterRole.length > 0) params.append('role', filterRole.join(','))
    if (filterStatus.length > 0) params.append('status', filterStatus.join(','))
    if (filterGender.length > 0) params.append('gender', filterGender.join(','))
    if (filterCountry) params.append('country', filterCountry)
    if (filterCity) params.append('city', filterCity)
    if (filterIsVerified !== null) params.append('isVerified', filterIsVerified)
    if (filterIsAdmin !== null) params.append('isAdmin', filterIsAdmin)
    if (filterPreferredLanguage) params.append('preferredLanguage', filterPreferredLanguage)
    if (filterCreatedFrom) params.append('createdFrom', filterCreatedFrom)
    if (filterCreatedTo) params.append('createdTo', filterCreatedTo)
    if (filterUpdatedFrom) params.append('updatedFrom', filterUpdatedFrom)
    if (filterUpdatedTo) params.append('updatedTo', filterUpdatedTo)
    if (filterBirthDateFrom) params.append('birthDateFrom', filterBirthDateFrom)
    if (filterBirthDateTo) params.append('birthDateTo', filterBirthDateTo)
    if (filterHasEnrollments !== null) params.append('hasEnrollments', filterHasEnrollments)
    if (filterHasCertificates !== null) params.append('hasCertificates', filterHasCertificates)
    if (filterHasPayments !== null) params.append('hasPayments', filterHasPayments)
    if (filterEnrolledInCourseId) params.append('enrolledInCourseId', filterEnrolledInCourseId)
    if (filterIncludeDeleted) params.append('includeDeleted', 'true')
    if (filterOnlyDeleted) params.append('onlyDeleted', 'true')
    params.append('sortBy', filterSortBy)
    params.append('sortOrder', filterSortOrder)
    
    return params.toString()
  }

  const handleExport = async (format) => {
    setIsExporting(true)
    try {
      const params = buildExportParams(format)
      const blob = await api.get(`${endpoints.usersExport}?${params}`, { responseType: 'blob' })
      
      // Extract filename from Content-Disposition header
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      const filename = format === 'csv' ? 'users.csv' : 'users.xlsx'
      a.download = filename
      a.click()
      window.URL.revokeObjectURL(url)
      
      toastUtils.success(`Export ${format.toUpperCase()} réussi`)
    } catch (error) {
      toastUtils.error(error?.message || "Erreur lors de l'export")
    } finally {
      setIsExporting(false)
    }
  }

  // Récupération des vraies données depuis la base avec pagination
  const queryParams = buildQueryParams()
  const { data: usersData, loading, error, execute: refetchUsers } = useGet(`${endpoints.users}?${queryParams}`)

  // Extraire les métadonnées de pagination quand usersData change
  useEffect(() => {
    if (usersData) {
      console.log('[UserManagementPage] usersData reçu:', usersData)
      console.log('[UserManagementPage] usersData.total:', usersData.total)
      console.log('[UserManagementPage] usersData.totalPages:', usersData.totalPages)
      console.log('[UserManagementPage] usersData.page:', usersData.page)
      
      if (usersData.total !== undefined) {
        setTotal(usersData.total)
        console.log('[UserManagementPage] total mis à jour:', usersData.total)
      }
      if (usersData.totalPages !== undefined) {
        setTotalPages(usersData.totalPages)
        console.log('[UserManagementPage] totalPages mis à jour:', usersData.totalPages)
      }
      // Ne pas mettre à jour currentPage depuis l'API pour éviter les boucles infinies
    }
  }, [usersData])

  // Rafraîchir les données quand les filtres ou la page changent
  useEffect(() => {
    if (refetchUsers) {
      refetchUsers()
    }
  }, [currentPage, pageLimit, searchTerm, filterRole, filterStatus, filterGender, filterCountry, filterCity, filterIsVerified, filterIsAdmin, filterPreferredLanguage, filterCreatedFrom, filterCreatedTo, filterUpdatedFrom, filterUpdatedTo, filterBirthDateFrom, filterBirthDateTo, filterHasEnrollments, filterHasCertificates, filterHasPayments, filterEnrolledInCourseId, filterIncludeDeleted, filterOnlyDeleted, filterSortBy, filterSortOrder])
  
  // Extraire les utilisateurs
  let users = []
  if (usersData) {
    if (Array.isArray(usersData)) {
      users = usersData
    } else if (usersData.users && Array.isArray(usersData.users)) {
      users = usersData.users
    } else if (usersData.data && Array.isArray(usersData.data)) {
      users = usersData.data
    }
  }
  
  console.log('[UserManagementPage] usersData:', usersData)
  console.log('[UserManagementPage] users:', users)
  console.log('[UserManagementPage] users.length:', users.length)
  console.log('[UserManagementPage] Exemple user:', users[0])

  if (users.length > 0 && users[0]) {
    const hasDeletedAt = 'deletedAt' in users[0]
    console.log('[UserManagementPage] Utilisateurs avec deletedAt (soft delete)?', hasDeletedAt)
    if (hasDeletedAt) {
      const deletedUsers = users.filter(u => u.deletedAt !== null && u.deletedAt !== undefined)
      console.log('[UserManagementPage] Utilisateurs soft deleted:', deletedUsers.length)
    }
  }
  
  // L'API gère maintenant le filtrage côté serveur
  console.log('[UserManagementPage] users:', users)
  console.log('[UserManagementPage] users.length:', users.length)
  
  // Fonction pour supprimer un utilisateur
  const deleteUser = async (id) => {
    return await api.delete(endpoints.deleteUser(id))
  }


  const handleViewUser = (user) => {
    setViewingUser(user)
  }

  const handleEditUser = (user) => {
    setEditingUser(user)
    setIsUserModalOpen(true)
  }

  const handleCreateUser = () => {
    setEditingUser(null)
    setIsUserModalOpen(true)
  }

  const handleUserSuccess = async () => {
    console.log('[UserManagementPage] handleUserSuccess appelé')
    setEditingUser(null)
    setIsUserModalOpen(false)
    
    // Réinitialiser à la page 1 pour voir les nouvelles données
    setCurrentPage(1)
    
    // Attendre un peu pour que le backend enregistre les modifications
    await new Promise(resolve => setTimeout(resolve, 500))
    
    console.log('[UserManagementPage] Rafraîchissement des données...')
    try {
      // Forcer le rafraîchissement en appelant refetchUsers
      if (refetchUsers) {
        await refetchUsers()
        console.log('[UserManagementPage] refetchUsers terminé')
      }
      
      // Attendre un peu pour que les données se mettent à jour
      await new Promise(resolve => setTimeout(resolve, 500))
      console.log('[UserManagementPage] Données rafraîchies, usersData devrait être à jour')
    } catch (error) {
      console.error('[UserManagementPage] Erreur lors du rafraîchissement:', error)
    }
  }

  const handleDeleteUser = async (user) => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${user.name} ?`)) {
      try {
        console.log('[UserManagementPage] Suppression de l\'utilisateur:', user.id, user.email)
        const response = await deleteUser(user.id)
        console.log('[UserManagementPage] Réponse de suppression:', response)
        console.log('[UserManagementPage] Utilisateur supprimé:', user.email)
        
        toastUtils.success("Utilisateur supprimé avec succès !")
        
        // Réinitialiser à la page 1 pour voir les données mises à jour
        setCurrentPage(1)
        
        // Attendre un peu avant de rafraîchir pour que le backend enregistre la suppression
        await new Promise(resolve => setTimeout(resolve, 500))
        await refetchUsers()
        console.log('[UserManagementPage] Liste rafraîchie après suppression')
      } catch (error) {
        console.error('[UserManagementPage] Erreur suppression:', error)
        console.error('[UserManagementPage] Détails erreur:', {
          message: error.message,
          status: error.status,
          details: error.details
        })
        toastUtils.error(error.message || "Erreur lors de la suppression de l'utilisateur")
      }
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "Actif": return "bg-blue-50 text-[#203a8a]"
      case "Suspendu": return "bg-gray-100 text-gray-800"
      case "Inactif": return "bg-gray-100 text-gray-800"
      case "Banni": return "bg-gray-100 text-gray-800"
      default: return "bg-gray-100 text-gray-800"
    }
  }

  // Afficher un loader pendant le chargement
  if (loading) {
    return (
      <div className="flex min-h-screen bg-gray-100 overflow-x-hidden">
        <AdminSidebar />
        <main className="flex-1 p-8 ml-64 max-w-full overflow-x-hidden flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#203a8a] mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement des utilisateurs...</p>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen bg-gray-100 overflow-x-hidden">
      <AdminSidebar />

      {/* Main Content */}
      <main className="flex-1 p-8 ml-64 max-w-full overflow-x-hidden">
        <AdminHeader
          title="Gestion des étudiants"
          subtitle="Gérer les étudiants de la plateforme"
          showCreateButton={true}
          createButtonText="Créer un étudiant"
          onCreateClick={handleCreateUser}
        />

        {/* Filters and Search */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 mb-8">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-grow">
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher (email, nom, téléphone, ville, pays, adresse)..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm w-full focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              <Filter size={16} />
              {showAdvancedFilters ? 'Masquer les filtres' : 'Filtres avancés'}
            </button>
            <select
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-transparent"
              value={pageLimit}
              onChange={(e) => setPageLimit(parseInt(e.target.value))}
            >
              <option value="10">10 par page</option>
              <option value="20">20 par page</option>
              <option value="50">50 par page</option>
              <option value="100">100 par page</option>
            </select>
            <select
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-transparent"
              value={filterSortBy}
              onChange={(e) => setFilterSortBy(e.target.value)}
            >
              <option value="createdAt">Date de création</option>
              <option value="updatedAt">Date de mise à jour</option>
              <option value="name">Nom</option>
              <option value="email">Email</option>
              <option value="role">Rôle</option>
              <option value="status">Statut</option>
              <option value="country">Pays</option>
              <option value="city">Ville</option>
              <option value="birthDate">Date de naissance</option>
              <option value="enrollments">Inscriptions</option>
            </select>
            <select
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-transparent"
              value={filterSortOrder}
              onChange={(e) => setFilterSortOrder(e.target.value)}
            >
              <option value="desc">Décroissant</option>
              <option value="asc">Croissant</option>
            </select>
            <div className="flex gap-2 ml-auto">
              <button
                onClick={() => handleExport('xlsx')}
                disabled={isExporting}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg text-sm font-medium flex items-center gap-2"
              >
                <Download size={16} />
                {isExporting ? 'Export...' : 'Excel'}
              </button>
              <button
                onClick={() => handleExport('csv')}
                disabled={isExporting}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg text-sm font-medium flex items-center gap-2"
              >
                <Download size={16} />
                {isExporting ? 'Export...' : 'CSV'}
              </button>
            </div>
          </div>

          {/* Advanced Filters */}
          {showAdvancedFilters && (
            <div className="mt-6 pt-6 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Role Multi-select */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Rôles</label>
                <div className="space-y-2">
                  {['STUDENT', 'INSTRUCTOR', 'ADMIN', 'MODERATOR'].map(role => (
                    <label key={role} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={filterRole.includes(role)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFilterRole([...filterRole, role])
                          } else {
                            setFilterRole(filterRole.filter(r => r !== role))
                          }
                        }}
                        className="rounded border-gray-300 text-[#203a8a] focus:ring-[#203a8a]"
                      />
                      {role}
                    </label>
                  ))}
                </div>
              </div>

              {/* Status Multi-select */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Statuts</label>
                <div className="space-y-2">
                  {['ACTIVE', 'INACTIVE', 'SUSPENDED', 'BANNED'].map(status => (
                    <label key={status} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={filterStatus.includes(status)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFilterStatus([...filterStatus, status])
                          } else {
                            setFilterStatus(filterStatus.filter(s => s !== status))
                          }
                        }}
                        className="rounded border-gray-300 text-[#203a8a] focus:ring-[#203a8a]"
                      />
                      {status}
                    </label>
                  ))}
                </div>
              </div>

              {/* Gender Multi-select */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Genre</label>
                <div className="space-y-2">
                  {['MALE', 'FEMALE', 'OTHER'].map(gender => (
                    <label key={gender} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={filterGender.includes(gender)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFilterGender([...filterGender, gender])
                          } else {
                            setFilterGender(filterGender.filter(g => g !== gender))
                          }
                        }}
                        className="rounded border-gray-300 text-[#203a8a] focus:ring-[#203a8a]"
                      />
                      {gender}
                    </label>
                  ))}
                </div>
              </div>

              {/* Country */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Pays</label>
                <input
                  type="text"
                  placeholder="Pays..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-transparent"
                  value={filterCountry}
                  onChange={(e) => setFilterCountry(e.target.value)}
                />
              </div>

              {/* City */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Ville</label>
                <input
                  type="text"
                  placeholder="Ville..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-transparent"
                  value={filterCity}
                  onChange={(e) => setFilterCity(e.target.value)}
                />
              </div>

              {/* Preferred Language */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Langue préférée</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-transparent"
                  value={filterPreferredLanguage}
                  onChange={(e) => setFilterPreferredLanguage(e.target.value)}
                >
                  <option value="">Toutes</option>
                  <option value="fr">Français</option>
                  <option value="en">English</option>
                </select>
              </div>

              {/* Is Verified */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Vérifié</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-transparent"
                  value={filterIsVerified === null ? '' : filterIsVerified}
                  onChange={(e) => setFilterIsVerified(e.target.value === '' ? null : e.target.value === 'true')}
                >
                  <option value="">Tous</option>
                  <option value="true">Oui</option>
                  <option value="false">Non</option>
                </select>
              </div>

              {/* Is Admin */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Admin</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-transparent"
                  value={filterIsAdmin === null ? '' : filterIsAdmin}
                  onChange={(e) => setFilterIsAdmin(e.target.value === '' ? null : e.target.value === 'true')}
                >
                  <option value="">Tous</option>
                  <option value="true">Oui</option>
                  <option value="false">Non</option>
                </select>
              </div>

              {/* Date Ranges */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Créé du</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-transparent"
                  value={filterCreatedFrom}
                  onChange={(e) => setFilterCreatedFrom(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Créé au</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-transparent"
                  value={filterCreatedTo}
                  onChange={(e) => setFilterCreatedTo(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Mis à jour du</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-transparent"
                  value={filterUpdatedFrom}
                  onChange={(e) => setFilterUpdatedFrom(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Mis à jour au</label>
                <input
                  type="date"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-transparent"
                  value={filterUpdatedTo}
                  onChange={(e) => setFilterUpdatedTo(e.target.value)}
                />
              </div>

              {/* Relational Filters */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">A des inscriptions</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-transparent"
                  value={filterHasEnrollments === null ? '' : filterHasEnrollments}
                  onChange={(e) => setFilterHasEnrollments(e.target.value === '' ? null : e.target.value === 'true')}
                >
                  <option value="">Tous</option>
                  <option value="true">Oui</option>
                  <option value="false">Non</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">A des certificats</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-transparent"
                  value={filterHasCertificates === null ? '' : filterHasCertificates}
                  onChange={(e) => setFilterHasCertificates(e.target.value === '' ? null : e.target.value === 'true')}
                >
                  <option value="">Tous</option>
                  <option value="true">Oui</option>
                  <option value="false">Non</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">A des paiements</label>
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-transparent"
                  value={filterHasPayments === null ? '' : filterHasPayments}
                  onChange={(e) => setFilterHasPayments(e.target.value === '' ? null : e.target.value === 'true')}
                >
                  <option value="">Tous</option>
                  <option value="true">Oui</option>
                  <option value="false">Non</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Inscrit au cours (ID)</label>
                <input
                  type="text"
                  placeholder="UUID du cours..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#203a8a] focus:border-transparent"
                  value={filterEnrolledInCourseId}
                  onChange={(e) => setFilterEnrolledInCourseId(e.target.value)}
                />
              </div>

              {/* Soft-delete Filters */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Inclure supprimés</label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={filterIncludeDeleted}
                    onChange={(e) => setFilterIncludeDeleted(e.target.checked)}
                    className="rounded border-gray-300 text-[#203a8a] focus:ring-[#203a8a]"
                  />
                  Inclure les utilisateurs supprimés
                </label>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Corbeille</label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={filterOnlyDeleted}
                    onChange={(e) => setFilterOnlyDeleted(e.target.checked)}
                    className="rounded border-gray-300 text-[#203a8a] focus:ring-[#203a8a]"
                  />
                  Afficher uniquement la corbeille
                </label>
              </div>

              {/* Clear Filters */}
              <div className="flex items-end">
                <button
                  onClick={() => {
                    setSearchTerm("")
                    setFilterRole([])
                    setFilterStatus([])
                    setFilterGender([])
                    setFilterCountry("")
                    setFilterCity("")
                    setFilterIsVerified(null)
                    setFilterIsAdmin(null)
                    setFilterPreferredLanguage("")
                    setFilterCreatedFrom("")
                    setFilterCreatedTo("")
                    setFilterUpdatedFrom("")
                    setFilterUpdatedTo("")
                    setFilterBirthDateFrom("")
                    setFilterBirthDateTo("")
                    setFilterHasEnrollments(null)
                    setFilterHasCertificates(null)
                    setFilterHasPayments(null)
                    setFilterEnrolledInCourseId("")
                    setFilterIncludeDeleted(false)
                    setFilterOnlyDeleted(false)
                    setFilterSortBy("createdAt")
                    setFilterSortOrder("desc")
                    setCurrentPage(1)
                  }}
                  className="w-full px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded-lg text-sm font-medium hover:bg-red-100"
                >
                  Réinitialiser tous les filtres
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Users Table */}
        <UsersTable 
          users={users}
          onView={handleViewUser}
          onEdit={handleEditUser}
          onDelete={handleDeleteUser}
        />

        {/* Pagination Controls */}
        {console.log('[UserManagementPage] État pagination - totalPages:', totalPages, 'currentPage:', currentPage, 'total:', total, 'condition totalPages > 1:', totalPages > 1)}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 mt-6 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Affichage de <span className="font-semibold">{(currentPage - 1) * pageLimit + 1}</span> à <span className="font-semibold">{Math.min(currentPage * pageLimit, total)}</span> sur <span className="font-semibold">{total}</span> utilisateurs
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                Précédent
              </button>
              <span className="px-4 py-2 text-sm font-medium text-gray-700">
                Page {currentPage} sur {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                Suivant
              </button>
            </div>
          </div>
      </main>

      {/* User Form Modal */}
      <UserFormModal
        isOpen={isUserModalOpen}
        onClose={() => setIsUserModalOpen(false)}
        user={editingUser}
        onSuccess={handleUserSuccess}
        defaultRole="student"
      />

      {/* Learner Detail Modal (vue détaillée complète) */}
      <LearnerDetailModal
        isOpen={!!viewingUser}
        userId={viewingUser?.id}
        onClose={() => setViewingUser(null)}
      />

    </div>
  )
}
